/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  BuilderNavigationService,
  BuilderUserContext,
  ComponentItemFinderDialogConfig,
  componentItemFinderDialogRoute,
  ComponentItemFinderListItem,
  updatePrivilegedControls,
} from '@gh/builder-shared';
import {
  BuilderPermission,
  ComponentItemFilterMode,
  PRIVILEGED_EP_ESTIMATING_FEE_EXCLUSIONS,
  SystemRoleBit,
} from '@gh/builder-shared-data';
import {
  filterableForm,
  FilterableFormState,
  FormStateBuilder,
  getAllNewSelectedEntities,
  getParentRouteUrl,
  getRouteInitData,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { filterTracker, RouteDataService } from '@gh/core-data';
import {
  DialogService,
  FormMode,
  noAnswer,
  NotificationService,
  WaitingContext,
  WaitingMode,
  WaitingService,
  yesAnswer,
  yesNoCancelConfirmation,
  yesNoConfirmation,
} from '@gh/core-ui';
import {
  DEFAULT_FEE_EXCLUSIONS_FILTER,
  FeeExclusionsListItem,
  FeeExclusionsListPutItemDto,
} from '../../resources/fee-exclusions-list-item.model';
import { FeeExclusionsGridDefinition } from './fee-exclusions-grid-definition.service';
import {
  EstimatingFeeExclusionsTabBusinessService,
} from '../../resources/estimating-fee-exclusions-tab-business.service';
import { filter$, map$, switchMap$ } from '@gh/rx/operators';
import { Observable } from 'rxjs';

@Component({
  standalone: false,
  selector:      'gh-fee-exclusions-list-tab',
  templateUrl:   'fee-exclusions-list-tab.component.html',
  styleUrls:     ['fee-exclusions-list-tab.component.scss'],
  viewProviders: [FeeExclusionsGridDefinition],
})
export class FeeExclusionsListTabComponent implements OnInit {

  listItems: FeeExclusionsListItem[] = [];
  addedItems: FeeExclusionsListItem[] = [];
  deletedItems: FeeExclusionsListItem[] = [];
  feeExclusionsList: FeeExclusionsListItem[] = [];
  activeLoadedData: FeeExclusionsListItem[] = [];
  loading: boolean;
  form: FilterableFormState<{
    showDeleted: boolean;
    searchString: string;
    mode: FormMode;
  }>;
  checkList: any[] = [];
  get isShowDeleteSelected(): boolean {
    return !!this.checkList.length;
  }

  get isModifyExist(): boolean {
    return !this.isCorporateOrMaster && this.builderContext.hasPermission(BuilderPermission.Setup.Estimating.FeeExclusions.EDIT);
  }

  get isModifyCanBeEnabled(): boolean {
    return this.builderContext.hasPermission(BuilderPermission.Setup.Estimating.FeeExclusions.EDIT);
  }

  get hasChanges(): boolean {
    return !!this.addedItems.length || !!this.deletedItems.length;
  }

  get parentRoute(): string {
    return getParentRouteUrl(this.route);
  }

  get hasEditPermission(): boolean {
    return this.form.mode === FormMode.EditMode && (
      this.builderContext.hasSystemRole(SystemRoleBit.Corporate)
      || this.builderContext.hasSystemRole(SystemRoleBit.Master)
      || this.builderContext.hasPermission(BuilderPermission.Setup.Estimating.FeeExclusions.EDIT));
  }

  get isCorporateOrMaster(): boolean {
    return this.builderContext.hasSystemRole(SystemRoleBit.Master) || this.builderContext.hasSystemRole(SystemRoleBit.Corporate);
  }

  get deactivateFlow(): any {
    return this.onProcess();
  }

  constructor(public gridDefinition: FeeExclusionsGridDefinition,
    private builderContext: BuilderUserContext,
    private formStateBuilder: FormStateBuilder,
    private route: ActivatedRoute,
    private routeData: RouteDataService,
    private waitingContext: WaitingContext,
    private waitingService: WaitingService,
    private navigationService: BuilderNavigationService,
    private notifications: NotificationService,
    private businessService: EstimatingFeeExclusionsTabBusinessService,
    private dialogService: DialogService,
  ) {
  }

  /**
   * Init component
   */
  ngOnInit(): void {
    const { gridLayout } = getRouteInitData(this.route);

    this.gridDefinition.bind(gridLayout);

    this.form = filterableForm(this.formStateBuilder.buildPlain({
      value:    DEFAULT_FEE_EXCLUSIONS_FILTER,
      mode:     this.isCorporateOrMaster ?  FormMode.EditMode : FormMode.ViewMode,
      privilegedEntryPoints: [PRIVILEGED_EP_ESTIMATING_FEE_EXCLUSIONS],
      children: {
        searchString: void 0,
      },
    }));
    const tracker = this.routeData.tracker(filterTracker(this.form));
    tracker.listen(this.route);
    tracker.filterChanges.subscribe(() => this.load());
    this.setModifyEnabled(true);
  }

  onDelete(item: FeeExclusionsListItem): void {
    this.dialogService.confirmation(
      yesNoConfirmation('msg_fee_exclusions_item_delete')).pipe(
      filter$(yesAnswer)).subscribe(() => {
      if (this.addedItems.find((addedItem) => addedItem.id === item.id)) {
        this.addedItems = this.addedItems.filter((addedItem) => addedItem.id !== item.id);
        this.setFeeExclusionsList();
        return;
      }
      this.deletedItems.push(item);
      this.listItems = this.listItems.filter((deletedItem) => deletedItem.id !== item.id);
      this.setFeeExclusionsList();
    });
  }

  onProcess(): void {
    const process = this.waitingContext.start(WaitingMode.Blocked);
    this.completeFlow().subscribe(
      () => {
        process.stop();
        this.load();
      }, (err) => { process.stop(); this.notifications.processAll(err); },

    );
  }

  completeFlow(): Observable<boolean> {
    const addedList = this.addedItems.map((item) => (
      { itemId: item.id, itemCode: item.code, dirtyType: 'NEW' } as FeeExclusionsListPutItemDto
    ));
    const deletedList = this.deletedItems.map((item) => (
      { itemId: item.id, itemCode: item.code, dirtyType: 'DELETED' } as FeeExclusionsListPutItemDto
    ));
    const object = {
      privilegedTokens: {
        'EstimatingFeeExclusions': this.form.group.get('privilegedTokens.EstimatingFeeExclusions')?.value,
      },
      items: [...addedList, ...deletedList],
    } as any;
    return  this.businessService.update(object).pipe(map$((res) => {
      this.form.group.get('privilegedTokens.EstimatingFeeExclusions')?.setValue(null);
      this.setFromMode();
      this.setModifyEnabled(true);
      this.addedItems = [];
      this.deletedItems = [];
      this.notifications.success('msg_recalculation_jobs_task_added');
      this.notifications.success('msg_saved_successfully');
      return !!res;
    }));
  }

  canDeactivate(): Observable<boolean> | boolean {
    if (!this.hasChanges) {
      return true;
    }
    const confirmation$ = this.dialogService.confirmation(yesNoCancelConfirmation('msg_unsaved_info'));

    confirmation$.pipe(filter$(yesAnswer),
                       switchMap$(() => this.completeFlow().pipe(
                         this.waitingService.operator()))).subscribe(() => {}, this.notifications.processAll);
    confirmation$.pipe(filter$(noAnswer)).subscribe(() => true);
    return confirmation$.pipe(map$((res) => !!res));
  }

  onCancel(): void {
    if (this.hasChanges) {
      const confirmation$ = this.dialogService.confirmation(yesNoCancelConfirmation('msg_unsaved_info'));

      confirmation$.pipe(filter$(yesAnswer)).subscribe(() => this.onProcess());
      confirmation$.pipe(filter$(noAnswer)).subscribe(() => {
        this.addedItems = [];
        this.deletedItems = [];
        this.setFeeExclusionsList();
      });
    }
  }

  onSearch(): void {
    this.form.emitFilter();
  }

  onAdd(): void {
    this.dialogService.openWithInput(componentItemFinderDialogRoute, <ComponentItemFinderDialogConfig>{
      filterMode:        ComponentItemFilterMode.Fee,
      disableFilterMode: true,
      multiselection:    true,
      preselected:       [],
    }).afterClosed().pipe(
      filter$(Boolean),
      map$(getAllNewSelectedEntities),
    )
      .subscribe(
        (items: ComponentItemFinderListItem[]) => {
          items.forEach((item) => {
            this.addedItems.push({
              active: true,
              code:   item.code,
              cost:   item.cost,
              costUpdated: null,
              formula:     !!item.formula,
              id:          item.id,
              name:        item.name,
              picture:     false,
              salesTax:    item.isSalesTax,
              uomCode:     item.uomCode,
              uomName:     '',
              isNew:       true,
            });
          });
          this.setFeeExclusionsList();
        },
        this.notifications.processAll,
      );
  }

  goToAel(entityId?: ID, entityType?: string, label?: string): void {
    this.navigationService.goToAel({
      entityId:   entityId || void 0,
      entityType: entityType ? entityType : 'FEE_EXCLUSION',
      label:      label ? label : 'lbl_fee_exclusions',
    });
  }

  onPrivilegedTokenReceivedPermitDate(): void {
    this.form.setMode(FormMode.EditMode);
    this.setModifyEnabled(false);
    updatePrivilegedControls(this.form, PRIVILEGED_EP_ESTIMATING_FEE_EXCLUSIONS);
  }

  onDeleteSelected(): void {
    this.dialogService.confirmation(
      yesNoConfirmation('msg_fee_exclusions_items_delete')).pipe(
      filter$(yesAnswer)).subscribe(() => {
        this.addedItems = this.addedItems.filter((item) => !this.checkList.includes(item.id));
        this.setFeeExclusionsList();
        const deletedItemList = this.feeExclusionsList.filter((item) => this.checkList.includes(item.id));
        this.deletedItems = [...this.deletedItems, ...deletedItemList];
        this.listItems = this.listItems.filter((item) => !this.checkList.includes(item.id));
        this.checkList = [];
        this.setFeeExclusionsList();
    });
  }

  isActionButtonsVisible(): boolean {
    return !this.checkList.length ;
  }

  rowClicked(event: any): void {
    if (event.preventDefault) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (event.event.shiftKey) {
      this.onCheckChange(event.data.id);
      return;
    }
  }

  onToggleSelectAll(): void {
    if (!this.hasEditPermission) {
      return;
    }
    if (!this.checkList.length || (!!this.checkList.length && this.checkList.length < this.activeLoadedData.length)) {
      this.checkList = this.activeLoadedData?.map((item) => item.id);
      return;
    }
    this.checkList = [];
  }

  onCheckChange(id: ID): void {
    if (!this.hasEditPermission) {
      return;
    }

    const targetItem = this.feeExclusionsList.find((item) => item.id === id);
    if (!targetItem || !this.canBeSelected(targetItem)) {
      return;
    }
    if (this.checkList.includes(id)) {
      this.checkList.splice(this.checkList.indexOf(id), 1);
      this.checkList = [...this.checkList];
      return;
    }
    this.checkList = [...this.checkList, id];
  }

  canBeSelected(item: FeeExclusionsListItem): boolean {
    return item.active;
  }

  private load(): void {
    this.loading = true;
    this.businessService.get(this.form.submitValue).subscribe(
      (items) => {
        this.listItems = items;
        this.setFeeExclusionsList();
        this.loading = false;
      },
      this.notifications.processAll,
    );
  }

  private setFeeExclusionsList(): void {
    this.feeExclusionsList = [...this.addedItems, ...this.listItems];
    this.activeLoadedData = this.feeExclusionsList.filter(this.canBeSelected);
  }

  private setFromMode(): void {
    if (this.isCorporateOrMaster) {
      this.form.setMode(FormMode.EditMode);
      return;
    }
    if (this.form.group.get('privilegedTokens.EstimatingFeeExclusions')?.value) {
      this.form.setMode(FormMode.EditMode);
      return;
    }
    this.form.setMode(FormMode.ViewMode);
  }

  private setModifyEnabled(enable: boolean): void {
    if (!this.isModifyCanBeEnabled) {
      return;
    }
    const control = this.form.group.get('privilegedTokens.EstimatingFeeExclusions');
    if (enable) {
      control?.enable();
      return;
    }
    control?.disable();
  }
}
